import { lazy } from 'react'
import { Translation } from 'react-i18next'
import { Navigate, Outlet, Route } from 'react-router-dom'

import { typeUserViewScheduleEnum } from '@/modules/schedule/domain/models/TypeUserViewSchedule'
import SituationalIcon from '@/shared/components/icons/situational-icon'
import BathroomHangerIcon from '@/ui/assets/BathroomHangerIcon'
import BodyInspectionIcon from '@/ui/assets/BodyInspectionIcon'
import CalendarPersonIcon from '@/ui/assets/CalendarPersonIcon'
import ChatMessageAddSquareIcon from '@/ui/assets/ChatMessageAddSquareIcon'
import ChatMessageIcon from '@/ui/assets/ChatMessageIcon'
import CommentsNewIcon from '@/ui/assets/CommentsNewIcon'
import DocumentPaperPenIcon from '@/ui/assets/DocumentPaperPenIcon'
import FolderImageIcon from '@/ui/assets/FolderImageIcon'
import FoodIcon from '@/ui/assets/FoodIcon'
import FourSquareIcon from '@/ui/assets/FourSquareIcon'
import HomeIcon from '@/ui/assets/HomeIcon'
import InterferingBehaviorIcon from '@/ui/assets/InterferingBehaviorIcon'
import LiquidIcon from '@/ui/assets/LiquidIcon'
import ManagementCollectionIcon from '@/ui/assets/ManagementCollectionIcon'
import MedicalRecord from '@/ui/assets/MedicalRecord'
import MyGroupsIcon from '@/ui/assets/MyGroupsIcon'
import NotebookIcon from '@/ui/assets/NotebookIcon'
import PersonIcon from '@/ui/assets/PersonIcon'
import RemediesIcon from '@/ui/assets/RemediesIcon'
import SessionsIcon from '@/ui/assets/SessionsIcon'
import SleepStarCircleIcon from '@/ui/assets/SleepStarCircleIcon'
import TicketPaperIcon from '@/ui/assets/TicketPaperIcon'
import ToyIcon from '@/ui/assets/ToyIcon'
import LocationBasedRemountComponent from '@/ui/components/LocationBasedRemountComponent'
import BelongItemsProvider from '@/ui/contexts/BelongsItemsContext'
import { CopyDataDriveProvider } from '@/ui/contexts/CloneDataDriveGroupToClient'
import PatientProvider from '@/ui/contexts/PatientContext'
import { PresenceProvider } from '@/ui/contexts/PresenceContext'
import { ScheduleProvider } from '@/ui/contexts/ScheduleContext'
import { TicketChatProvider } from '@/ui/contexts/TicketChatContext'
import TicketEvaluationProvider from '@/ui/contexts/TicketEvaluationContext'
import { ConfigClientModulesManager } from '@/ui/hoc/ConfigClientModules/ConfigClientModulesManager'
import { FeatureFlagManager } from '@/ui/hoc/FeatureFlag/FeatureFlagManager'
import { FeatureFlagManagerWithDefaultPage } from '@/ui/hoc/FeatureFlag/FeatureFlagManagerWithDefaultPage'
import { MultiSelectionProvider } from '@/ui/hoc/MultiSelection'
import { UserPermissionManager } from '@/ui/hoc/UserPermission/UserPermissionManager'
import UserPermissionShowComponent from '@/ui/hoc/UserPermission/UserPermissionShowComponent'
import FoodStockProvider from '@/ui/modules/operational/contexts/FoodStockContext'
import { BodyInspectionProvider } from '@/ui/modules/operational/views/BodyInspection/DailyTable/contexts/BodyInspectionFilterContext'
import CAA from '@/ui/modules/operational/views/CAA'
import SessionsManagementProvider from '@/ui/modules/operational/views/SessionManager/contexts/SessionsManagementContext'
import {
  therapistDynamicRoutes,
  therapistRouteNames
} from '@/ui/routes/routesNames/therapistRouteNames'

import GalleryProvider from '../../therapist/contexts/GalleryContext'
import { useUserPermission } from '../hooks/useUserPermission'

const ExploreAndInvitations = lazy(
  () =>
    import(
      '../../therapist/pages/groups/PostGroup/components/ExploreAndInvitations'
    )
)

const TicketDetails = lazy(
  () => import('@/ui/modules/operational/views/TicketDetails')
)
const UserTicket = lazy(
  () => import('@/ui/modules/operational/views/UserTicket')
)
const ScheduleTherapist = lazy(
  () => import('../../therapist/pages/sheduleTherapist')
)
const PhotosPatient = lazy(
  () => import('@/ui/modules/operational/views/Photos/PhotosPatient')
)

const Schedule = lazy(
  () => import('../../therapist/pages/patient-profile/schedule')
)
const PatientLayout = lazy(() => import('@/ui/layouts/PatientLayout'))
const GalleryUser = lazy(() => import('../../therapist/pages/galleryUser'))
const InternalLayout = lazy(() => import('@/ui/layouts/InternalLayout'))
const SessionManager = lazy(
  () => import('@/ui/modules/operational/views/SessionManager')
)
// TEMP REMOVE REPORT
// const DocumentsPatient = lazy(
//   () => import('@/ui/modules/operational/views/Documents')
// )
const InterferingBehavior = lazy(
  () => import('@/ui/modules/operational/views/InterferingBehavior')
)
const InterferingBehaviorPage = lazy(
  () =>
    import(
      '@/ui/modules/operational/views/InterferingBehavior/InterferingBehaviorPage'
    )
)
const Comments = lazy(() => import('@/ui/modules/operational/views/Comments'))
const CAAPatient = lazy(
  () => import('@/ui/modules/operational/views/CAAPatient')
)

const FoodScheduleProvider = lazy(
  () => import('@/ui/modules/operational/contexts/FoodScheduleTabsContext')
)
const Chat = lazy(() => import('../../therapist/pages/chat'))
const TicketChat = lazy(() => import('@/ui/modules/shared/TicketChat'))
const FileEditor = lazy(() => import('@/ui/modules/file-editor/views'))

const Group = lazy(() => import('../../therapist/pages/groups'))
// TEMP REMOVE REPORT
// const DocumentsPage = lazy(() => import('./pages/documents'))
const GroupFeed = lazy(
  () => import('@/ui/modules/operational/views/GroupsFeed')
)
const PatientSelector = lazy(() => import('@/ui/pages/PatientSelector'))
const PostDetails = lazy(() => import('../../therapist/pages/post-details'))
const Profile = lazy(() => import('@/therapist/pages/profile'))
const TaskPage = lazy(() => import('../../therapist/pages/tasks'))
const Gallery = lazy(
  () => import('../../therapist/pages/patient-profile/gallery')
)
const Bathroom = lazy(() => import('@operational-ui/views/Bathroom'))
const Belongs = lazy(() => import('@operational-ui/views/Belongs'))
const Food = lazy(() => import('@operational-ui/views/Food'))
const Remedy = lazy(() => import('@operational-ui/views/Remedy'))
const Liquids = lazy(() => import('@operational-ui/views/Liquids'))
const Restriction = lazy(() => import('@operational-ui/views/Restriction'))
const Sleep = lazy(() => import('@operational-ui/views/Sleep'))
const ProfileInfo = lazy(() => import('@/shared/pages/client'))
const ManagedPatientsFrequencies = lazy(
  () => import('@therapist-ui/views/ManagedPatientsFrequencies')
)
const BodyInspection = lazy(
  () => import('@/ui/modules/operational/views/BodyInspection')
)
const ConfigClient = lazy(
  () => import('@/ui/modules/operational/views/ConfigClient')
)
const PatientTickets = lazy(
  () => import('@/ui/modules/operational/views/PatientTickets')
)
const MedicalRecordPatient = lazy(
  () => import('@/therapist/pages/medicalRecord')
)

export default function TherapistRoutes() {
  const { checkPermission } = useUserPermission()

  const patientGalleryRoutes = (
    <>
      <Route
        path={therapistRouteNames.gallery}
        element={
          <UserPermissionShowComponent permission="DRIVE_VIEW">
            <MultiSelectionProvider>
              <CopyDataDriveProvider>
                <GalleryProvider>
                  <Gallery />
                </GalleryProvider>
              </CopyDataDriveProvider>
            </MultiSelectionProvider>
          </UserPermissionShowComponent>
        }
      />
      <Route
        path={therapistRouteNames.galleryItem}
        element={
          <UserPermissionShowComponent permission="DRIVE_VIEW">
            <MultiSelectionProvider>
              <CopyDataDriveProvider>
                <GalleryProvider>
                  <Gallery />
                </GalleryProvider>
              </CopyDataDriveProvider>
            </MultiSelectionProvider>
          </UserPermissionShowComponent>
        }
      />
      <Route
        path={therapistRouteNames.galleryFolder}
        element={
          <UserPermissionShowComponent permission="DRIVE_VIEW">
            <MultiSelectionProvider>
              <CopyDataDriveProvider>
                <GalleryProvider>
                  <Gallery />
                </GalleryProvider>
              </CopyDataDriveProvider>
            </MultiSelectionProvider>
          </UserPermissionShowComponent>
        }
      />
    </>
  )

  return (
    <>
      <Route
        element={
          <PresenceProvider>
            <TicketChatProvider>
              <TicketEvaluationProvider>
                <PatientProvider>
                  <Outlet />
                </PatientProvider>
              </TicketEvaluationProvider>
            </TicketChatProvider>
          </PresenceProvider>
        }
      >
        <Route
          path={therapistRouteNames.home}
          element={<Navigate to={therapistRouteNames.groups} replace />}
        />

        <Route
          element={
            <PatientProvider>
              <Translation>
                {(t) => (
                  <UserPermissionManager>
                    {({
                      checkPermissionAndReturn,
                      checkNotPermissionAndReturn,
                      checkPermission
                    }) => (
                      <InternalLayout
                        homeLink={therapistRouteNames.groups}
                        menuItems={[
                          {
                            name: t('internalLayout.general.home'),
                            icon: <HomeIcon />,
                            navigateTo: therapistRouteNames.groups
                          },
                          {
                            name: t('schedule'),
                            icon: <CalendarPersonIcon />,
                            navigateTo:
                              therapistDynamicRoutes.therapistSchedule()
                          },
                          {
                            name: t('internalLayout.operational.patients'),
                            icon: <PersonIcon />,
                            navigateTo: therapistRouteNames.patients
                          },
                          {
                            name: t('internalLayout.therapist.ticket'),
                            icon: <TicketPaperIcon />,
                            subMenu: [
                              {
                                name: t('internalLayout.therapist.openTicket'),
                                icon: <ChatMessageAddSquareIcon />,
                                navigateTo: therapistRouteNames.ticket
                              },
                              {
                                name: t('internalLayout.therapist.tasks'),
                                icon: <NotebookIcon />,
                                navigateTo: therapistRouteNames.tasks
                              }
                            ]
                          },
                          ...(checkPermission('MANAGEMENT_SPECIALTY') &&
                          checkPermission('MAIN_MANAGER')
                            ? [
                                {
                                  name: 'Gestão',
                                  icon: <ManagementCollectionIcon />,
                                  subMenu: [
                                    ...checkPermissionAndReturn(
                                      'SESSION_MANAGEMENT',
                                      {
                                        name: t(
                                          'internalLayout.operational.sessions'
                                        ),
                                        icon: <SessionsIcon />,
                                        navigateTo: therapistRouteNames.session
                                      }
                                    ),
                                    {
                                      name: t('internalLayout.operational.caa'),
                                      icon: <FourSquareIcon />,
                                      navigateTo: therapistRouteNames.caa
                                    },
                                    {
                                      name: t(
                                        'internalLayout.operational.interferingBehavior'
                                      ),
                                      icon: <InterferingBehaviorIcon />,
                                      navigateTo:
                                        therapistRouteNames.interferingBehavior
                                    }
                                  ]
                                }
                              ]
                            : [])
                        ]}
                        searchItems={[
                          {
                            name: 'Feed',
                            icon: <HomeIcon />,
                            route: therapistRouteNames.groupDetails,
                            isNavigatable: false,
                            isSearchable: false,
                            children: [
                              {
                                name: 'Drive do grupo',
                                icon: <HomeIcon />,
                                route: therapistRouteNames.groupDrive,
                                isSearchable: false,
                                isNavigatable: false
                              }
                            ]
                          },
                          {
                            name: t('internalLayout.general.home'),
                            icon: <HomeIcon />,
                            route: therapistRouteNames.groups,
                            children: [
                              {
                                name: 'Explorar e convites',
                                route: therapistRouteNames.explore,
                                module: t('internalLayout.general.home')
                              }
                            ]
                          },
                          {
                            name: t(
                              'patient.sidebar.items.interferingBehavior'
                            ),
                            icon: <InterferingBehaviorIcon />,
                            route:
                              therapistRouteNames.interferingBehaviorClient,
                            isSearchable: false,
                            isNavigatable: false
                          },
                          {
                            name: 'Gestão',
                            icon: <ManagementCollectionIcon />,
                            children: [
                              {
                                name: t('internalLayout.operational.sessions'),
                                icon: <SessionsIcon />,
                                route: therapistRouteNames.session,
                                module: 'Gestão'
                              }
                            ]
                          },
                          {
                            name: t('schedule'),
                            icon: <CalendarPersonIcon />,
                            route: therapistDynamicRoutes.therapistSchedule()
                          },
                          ...checkNotPermissionAndReturn('PATIENT_LIST_VIEW', {
                            name: t('internalLayout.operational.patients'),
                            icon: <PersonIcon />,
                            route: therapistRouteNames.patients,
                            isSearchable: false,
                            isNavigatable: false,
                            children: [
                              {
                                name: t('patient.sidebar.items.gallery'),
                                icon: <FolderImageIcon />,
                                route: therapistRouteNames.gallery,
                                isSearchable: false,
                                isNavigatable: false
                              }
                            ]
                          }),
                          ...checkPermissionAndReturn('PATIENT_LIST_VIEW', {
                            name: t('internalLayout.operational.patients'),
                            icon: <PersonIcon />,
                            route: therapistRouteNames.patients,
                            children: [
                              {
                                name: 'Gerenciamento de frequências',
                                icon: <PersonIcon />,
                                route: therapistRouteNames.frequenciesManager,
                                module: t('internalLayout.operational.patients')
                              },
                              {
                                name: t('patient.sidebar.items.schedule'),
                                icon: <CalendarPersonIcon />,
                                route: therapistRouteNames.clientSchedule,
                                isSearchable: false,
                                isNavigatable: false
                              },
                              {
                                name: t('patient.sidebar.items.food'),
                                icon: <FoodIcon />,
                                isSearchable: false,
                                isNavigatable: false,
                                children: [
                                  {
                                    name: t(
                                      'patient.sidebar.items.scheduleFood'
                                    ),
                                    route: therapistRouteNames.food,
                                    module: t('patient.sidebar.items.food'),
                                    isSearchable: false,
                                    isNavigatable: false
                                  },
                                  {
                                    name: t(
                                      'patient.sidebar.items.restrictionFood'
                                    ),
                                    route: therapistRouteNames.restriction,
                                    module: t('patient.sidebar.items.food'),
                                    isSearchable: false,
                                    isNavigatable: false
                                  }
                                ]
                              },
                              {
                                name: t('patient.sidebar.items.bathroom'),
                                icon: <BathroomHangerIcon />,
                                route: therapistRouteNames.bathroom,
                                isSearchable: false,
                                isNavigatable: false
                              },
                              {
                                name: t('patient.sidebar.items.caa'),
                                icon: <FourSquareIcon />,
                                route: therapistRouteNames.caaClient,
                                isSearchable: false,
                                isNavigatable: false
                              },
                              // {
                              //   name: t(
                              //     'internalLayout.operational.mediaSharing'
                              //   ),
                              //   icon: <CameraIcon />,
                              //   route: therapistRouteNames.photosClient,
                              //   isSearchable: false,
                              //   isNavigatable: false
                              // },
                              {
                                name: t(
                                  'patient.sidebar.items.interferingBehavior'
                                ),
                                icon: <InterferingBehaviorIcon />,
                                route:
                                  therapistRouteNames.interferingBehaviorClient,
                                isSearchable: false,
                                isNavigatable: false
                              },
                              {
                                name: t('patient.sidebar.items.tickets'),
                                icon: <TicketPaperIcon />,
                                route: therapistRouteNames.patientTicket,
                                isSearchable: false,
                                isNavigatable: false
                              },
                              {
                                name: t('patient.sidebar.items.comments'),
                                icon: <CommentsNewIcon />,
                                route: therapistRouteNames.comments,
                                isSearchable: false,
                                isNavigatable: false
                              },
                              {
                                name: t('patient.sidebar.items.configClient'),
                                icon: <SituationalIcon />,
                                route: therapistRouteNames.configClient,
                                isSearchable: false,
                                isNavigatable: false
                              },
                              // TEMP REMOVE REPORT
                              // {
                              //   name: t('patient.sidebar.items.documents'),
                              //   icon: <DocumentPaperPenIcon />,
                              //   route: therapistRouteNames.documents,
                              //   isSearchable: false,
                              //   isNavigatable: false
                              // },
                              {
                                name: t('patient.sidebar.items.gallery'),
                                icon: <FolderImageIcon />,
                                route: therapistRouteNames.gallery,
                                isSearchable: false,
                                isNavigatable: false
                              },
                              {
                                name: t('patient.sidebar.items.medicalRecord'),
                                icon: <MedicalRecord />,
                                route: therapistRouteNames.medicalSheet,
                                isSearchable: false,
                                isNavigatable: false
                              },
                              {
                                name: t('patient.sidebar.items.myGroups'),
                                icon: <MyGroupsIcon />,
                                route: therapistRouteNames.groupsFeed,
                                isSearchable: false,
                                isNavigatable: false
                              },
                              {
                                name: t('patient.sidebar.items.liquids'),
                                icon: <LiquidIcon />,
                                route: therapistRouteNames.liquids,
                                isSearchable: false,
                                isNavigatable: false
                              },
                              {
                                name: t('patient.sidebar.items.remedy'),
                                icon: <RemediesIcon />,
                                route: therapistRouteNames.remedy,
                                isSearchable: false,
                                isNavigatable: false
                              },
                              {
                                name: t('patient.sidebar.items.belongs'),
                                icon: <ToyIcon />,
                                route: therapistRouteNames.patientBelongs,
                                isSearchable: false,
                                isNavigatable: false
                              },
                              {
                                name: t('patient.sidebar.items.sleep'),
                                icon: <SleepStarCircleIcon />,
                                route: therapistRouteNames.sleep,
                                isSearchable: false,
                                isNavigatable: false
                              },
                              {
                                name: t('patient.sidebar.items.bodyInspection'),
                                icon: <BodyInspectionIcon />,
                                route: therapistRouteNames.bodyInspection,
                                isSearchable: false,
                                isNavigatable: false
                              }
                            ]
                          }),
                          {
                            name: t('internalLayout.therapist.ticket'),
                            children: [
                              {
                                name: 'Detalhes do chamado',
                                icon: <ChatMessageAddSquareIcon />,
                                route: therapistRouteNames.ticketDetails,
                                module: t('internalLayout.therapist.ticket'),
                                isSearchable: false,
                                isNavigatable: false
                              },
                              {
                                name: t('internalLayout.therapist.openTicket'),
                                icon: <ChatMessageAddSquareIcon />,
                                route: therapistRouteNames.ticket,
                                module: t('internalLayout.therapist.ticket')
                              },
                              {
                                name: t('internalLayout.therapist.tasks'),
                                icon: <NotebookIcon />,
                                route: therapistRouteNames.tasks,
                                module: t('internalLayout.therapist.ticket')
                              },
                              {
                                name: 'Chat de chamados',
                                icon: <NotebookIcon />,
                                route: therapistRouteNames.ticketChat,
                                module: t('internalLayout.therapist.ticket')
                              },
                              {
                                name: 'Chat de chamados',
                                icon: <NotebookIcon />,
                                route: therapistRouteNames.ticketChat,
                                module: t('internalLayout.therapist.ticket')
                              }
                            ]
                          },
                          {
                            name: 'Gestão',
                            icon: <ManagementCollectionIcon />,
                            children: [
                              {
                                name: t('internalLayout.operational.sessions'),
                                icon: <SessionsIcon />,
                                route: therapistRouteNames.session,
                                module: 'Gestão'
                              },
                              {
                                name: t('internalLayout.operational.caa'),
                                icon: <FourSquareIcon />,
                                route: therapistRouteNames.caa,
                                module: 'Gestão'
                              },
                              {
                                name: t(
                                  'internalLayout.operational.interferingBehavior'
                                ),
                                icon: <InterferingBehaviorIcon />,
                                route: therapistRouteNames.interferingBehavior,
                                module: 'Gestão'
                              }
                            ]
                          },
                          {
                            name: t('internalLayout.therapist.documents'),
                            icon: <DocumentPaperPenIcon />,
                            route: therapistRouteNames.documentsForTherapist
                          },
                          {
                            name: 'Perfil',
                            icon: <PersonIcon />,
                            route: therapistRouteNames.profileInfo,
                            isSearchable: false,
                            isNavigatable: false
                          },
                          {
                            name: 'Meu perfil',
                            icon: <PersonIcon />,
                            route: therapistRouteNames.profile
                          },
                          {
                            name: 'Chat',
                            icon: <ChatMessageIcon />,
                            route: therapistRouteNames.chat
                          },
                          {
                            name: 'Editor de arquivo',
                            icon: <DocumentPaperPenIcon />,
                            route: therapistRouteNames.fileEditor
                          },
                          {
                            name: 'Detalhes da publicação',
                            icon: <HomeIcon />,
                            route: therapistRouteNames.postDetails,
                            isSearchable: false,
                            isNavigatable: false
                          }
                        ]}
                      />
                    )}
                  </UserPermissionManager>
                )}
              </Translation>
            </PatientProvider>
          }
        >
          <Route path={therapistRouteNames.ticket} element={<UserTicket />} />
          <Route
            path={therapistRouteNames.ticketDetails}
            element={<TicketDetails />}
          />
          <Route
            path={therapistRouteNames.profileInfo}
            element={<ProfileInfo />}
          />
          <Route
            path={therapistRouteNames.ticketChat}
            element={<TicketChat />}
          />
          <Route path={therapistRouteNames.chat} element={<Chat />} />
          <Route
            path={therapistRouteNames.fileEditor}
            element={<FileEditor />}
          />
          <Route
            path={therapistRouteNames.groups}
            element={
              <LocationBasedRemountComponent stableParams={['filter']}>
                <Group />
              </LocationBasedRemountComponent>
            }
          />
          <Route
            path={therapistRouteNames.explore}
            element={<ExploreAndInvitations />}
          />
          <Route path={therapistRouteNames.tasks} element={<TaskPage />} />
          <Route
            path={therapistRouteNames.taskDetails}
            element={<TaskPage />}
          />
          <Route
            path={therapistRouteNames.schedule}
            element={
              <ScheduleProvider>
                <ScheduleTherapist />
              </ScheduleProvider>
            }
          />
          <Route
            path={therapistRouteNames.postDetails}
            element={<PostDetails />}
          />
          <Route path={therapistRouteNames.profile} element={<Profile />} />
          <Route
            path={therapistRouteNames.patients}
            element={<PatientSelector personaType="THERAPIST" />}
          />
          <Route
            path={therapistRouteNames.session}
            element={
              <SessionsManagementProvider>
                <SessionManager />
              </SessionsManagementProvider>
            }
          />
          <Route path={therapistRouteNames.caa} element={<CAA isManager />} />
          <Route
            path={therapistRouteNames.interferingBehavior}
            element={<InterferingBehaviorPage />}
          />

          {checkPermission('PATIENT_LIST_VIEW') ? <></> : patientGalleryRoutes}

          <Route
            element={
              <Translation>
                {(t) => (
                  <FeatureFlagManager>
                    {({ checkFlag }) => (
                      <ConfigClientModulesManager>
                        {({ checkPermissionModulesAndReturn }) => (
                          <UserPermissionManager>
                            {({ checkPermissionAndReturn }) => (
                              <PatientLayout
                                menuItems={(patientId) => [
                                  ...checkPermissionAndReturn('AGENDA_VIEW', {
                                    name: t('patient.sidebar.items.schedule'),
                                    icon: <CalendarPersonIcon />,
                                    navigateTo:
                                      therapistDynamicRoutes.schedule(patientId)
                                  }),
                                  ...(checkFlag('FOOD')
                                    ? checkPermissionModulesAndReturn('FOOD', {
                                        name: t('patient.sidebar.items.food'),
                                        icon: <FoodIcon />,
                                        subMenu: [
                                          {
                                            name: t(
                                              'patient.sidebar.items.scheduleFood'
                                            ),
                                            navigateTo:
                                              therapistDynamicRoutes.food(
                                                patientId
                                              )
                                          },
                                          {
                                            name: t(
                                              'patient.sidebar.items.restrictionFood'
                                            ),
                                            navigateTo:
                                              therapistDynamicRoutes.restriction(
                                                patientId
                                              )
                                          }
                                        ]
                                      })
                                    : []),
                                  ...(checkFlag('BATHROOM')
                                    ? checkPermissionModulesAndReturn(
                                        'BATHROOM',
                                        {
                                          name: t(
                                            'patient.sidebar.items.bathroom'
                                          ),
                                          icon: <BathroomHangerIcon />,
                                          navigateTo:
                                            therapistDynamicRoutes.bathroom(
                                              patientId
                                            )
                                        }
                                      )
                                    : []),
                                  ...checkPermissionModulesAndReturn('CAA', {
                                    name: t('patient.sidebar.items.caa'),
                                    icon: <FourSquareIcon />,
                                    navigateTo:
                                      therapistDynamicRoutes.caaClient(
                                        patientId
                                      )
                                  }),
                                  {
                                    name: t(
                                      'patient.sidebar.items.interferingBehavior'
                                    ),
                                    icon: <InterferingBehaviorIcon />,
                                    navigateTo:
                                      therapistDynamicRoutes.interferingBehavior(
                                        patientId
                                      )
                                  },
                                  {
                                    name: t('patient.sidebar.items.tickets'),
                                    icon: <TicketPaperIcon />,
                                    navigateTo:
                                      therapistDynamicRoutes.patientTicket(
                                        patientId
                                      )
                                  },
                                  {
                                    name: t('patient.sidebar.items.comments'),
                                    icon: <CommentsNewIcon />,
                                    navigateTo:
                                      therapistDynamicRoutes.comments(patientId)
                                  },
                                  ...checkPermissionAndReturn(
                                    'MANAGER_DAILY_ACCOMPANIMENT',
                                    {
                                      name: t(
                                        'patient.sidebar.items.configClient'
                                      ),
                                      icon: <SituationalIcon />,
                                      navigateTo:
                                        therapistDynamicRoutes.config(patientId)
                                    }
                                  ),
                                  // TEMP REMOVE REPORT
                                  // {
                                  //   name: t(
                                  //     'patient.sidebar.items.documents'
                                  //   ),
                                  //   icon: (
                                  //     <DocumentPaperPenIcon
                                  //       color={'#495057'}
                                  //
                                  //     />
                                  //   ),
                                  //   navigateTo:
                                  //     therapistDynamicRoutes.documents(
                                  //       patientId
                                  //     )
                                  // },
                                  ...checkPermissionAndReturn('DRIVE_VIEW', {
                                    name: t('patient.sidebar.items.gallery'),
                                    icon: <FolderImageIcon />,
                                    navigateTo:
                                      therapistDynamicRoutes.gallery(patientId)
                                  }),
                                  {
                                    name: t(
                                      'patient.sidebar.items.medicalRecord'
                                    ),
                                    icon: <MedicalRecord />,
                                    navigateTo:
                                      therapistDynamicRoutes.medicalSheet(
                                        patientId
                                      )
                                  },
                                  {
                                    name: t('patient.sidebar.items.myGroups'),
                                    icon: <MyGroupsIcon />,
                                    navigateTo:
                                      therapistDynamicRoutes.groupsFeed(
                                        patientId
                                      )
                                  },
                                  ...(checkFlag('LIQUIDS')
                                    ? checkPermissionModulesAndReturn(
                                        'LIQUIDS',
                                        {
                                          name: t(
                                            'patient.sidebar.items.liquids'
                                          ),
                                          icon: <LiquidIcon />,
                                          navigateTo:
                                            therapistDynamicRoutes.liquids(
                                              patientId
                                            )
                                        }
                                      )
                                    : []),
                                  ...(checkFlag('MEDICINES')
                                    ? checkPermissionModulesAndReturn(
                                        'MEDICINES',
                                        {
                                          name: t(
                                            'patient.sidebar.items.remedy'
                                          ),
                                          icon: <RemediesIcon />,
                                          navigateTo:
                                            therapistDynamicRoutes.remedy(
                                              patientId
                                            )
                                        }
                                      )
                                    : []),
                                  // ...checkPermissionModulesAndReturn('PHOTO', {
                                  //   name: t('patient.sidebar.items.photos'),
                                  //   icon: <CameraIcon />,
                                  //   navigateTo:
                                  //     therapistDynamicRoutes.photosClient(
                                  //       patientId
                                  //     )
                                  // }),
                                  ...(checkFlag('BELONGINGS')
                                    ? checkPermissionModulesAndReturn(
                                        'BELONGINGS',
                                        {
                                          name: t(
                                            'patient.sidebar.items.belongs'
                                          ),
                                          icon: <ToyIcon />,
                                          navigateTo:
                                            therapistDynamicRoutes.patientBelongs(
                                              patientId
                                            )
                                        }
                                      )
                                    : []),
                                  ...(checkFlag('SLEEP')
                                    ? checkPermissionModulesAndReturn('SLEEP', {
                                        name: t('patient.sidebar.items.sleep'),
                                        icon: <SleepStarCircleIcon />,
                                        navigateTo:
                                          therapistDynamicRoutes.sleep(
                                            patientId
                                          )
                                      })
                                    : []),
                                  ...(checkFlag('LESION')
                                    ? checkPermissionModulesAndReturn(
                                        'LESION',
                                        {
                                          name: t(
                                            'patient.sidebar.items.bodyInspection'
                                          ),
                                          icon: <BodyInspectionIcon />,
                                          navigateTo:
                                            therapistDynamicRoutes.bodyInspection(
                                              patientId
                                            )
                                        }
                                      )
                                    : [])
                                ]}
                              />
                            )}
                          </UserPermissionManager>
                        )}
                      </ConfigClientModulesManager>
                    )}
                  </FeatureFlagManager>
                )}
              </Translation>
            }
          >
            <Route
              element={
                <FeatureFlagManagerWithDefaultPage flags={['FOOD']}>
                  <FoodScheduleProvider>
                    <FoodStockProvider>
                      <Outlet />
                    </FoodStockProvider>
                  </FoodScheduleProvider>
                </FeatureFlagManagerWithDefaultPage>
              }
            >
              <Route path={therapistRouteNames.food} element={<Food />} />
              <Route
                path={therapistRouteNames.restriction}
                element={<Restriction />}
              />
            </Route>

            <Route
              path={therapistRouteNames.liquids}
              element={
                <FeatureFlagManagerWithDefaultPage flags={['LIQUIDS']}>
                  <Liquids />
                </FeatureFlagManagerWithDefaultPage>
              }
            />
            <Route
              path={therapistRouteNames.bathroom}
              element={
                <FeatureFlagManagerWithDefaultPage flags={['BATHROOM']}>
                  <Bathroom />
                </FeatureFlagManagerWithDefaultPage>
              }
            />
            <Route
              path={therapistRouteNames.caaClient}
              element={<CAAPatient />}
            />
            <Route
              path={therapistRouteNames.photosClient}
              element={<PhotosPatient />}
            />
            <Route
              path={therapistRouteNames.sleep}
              element={
                <FeatureFlagManagerWithDefaultPage flags={['SLEEP']}>
                  <Sleep />
                </FeatureFlagManagerWithDefaultPage>
              }
            />
            <Route
              path={therapistRouteNames.patientBelongs}
              element={
                <FeatureFlagManagerWithDefaultPage flags={['BELONGINGS']}>
                  <BelongItemsProvider>
                    <Belongs />
                  </BelongItemsProvider>
                </FeatureFlagManagerWithDefaultPage>
              }
            />
            <Route
              path={therapistRouteNames.interferingBehaviorClient}
              element={
                <PatientProvider>
                  <InterferingBehavior />
                </PatientProvider>
              }
            />
            <Route
              path={therapistRouteNames.remedy}
              element={
                <FeatureFlagManagerWithDefaultPage flags={['MEDICINES']}>
                  <Remedy />
                </FeatureFlagManagerWithDefaultPage>
              }
            />
            <Route
              path={therapistRouteNames.patientTicket}
              element={<PatientTickets />}
            />
            <Route
              path={therapistRouteNames.clientSchedule}
              element={
                <UserPermissionShowComponent permission="AGENDA_VIEW">
                  <ScheduleProvider>
                    <Schedule
                      userViewType={typeUserViewScheduleEnum.enum.PATIENT}
                    />
                  </ScheduleProvider>
                </UserPermissionShowComponent>
              }
            />
            {patientGalleryRoutes}
            <Route
              path={therapistRouteNames.configClient}
              element={<ConfigClient />}
            />
            <Route
              path={therapistRouteNames.medicalSheet}
              element={
                <PatientProvider>
                  <MedicalRecordPatient />
                </PatientProvider>
              }
            />
            <Route
              path={therapistRouteNames.bodyInspection}
              element={
                <BodyInspectionProvider>
                  <BodyInspection />
                </BodyInspectionProvider>
              }
            />
            <Route path={therapistRouteNames.comments} element={<Comments />} />
            <Route
              path={therapistRouteNames.groupsFeed}
              element={<GroupFeed />}
            />
            <Route path={therapistRouteNames.entryPatient} element={<></>} />
          </Route>

          <Route
            path={therapistRouteNames.frequenciesManager}
            element={
              <FeatureFlagManagerWithDefaultPage
                flags={['LIQUIDS', 'BATHROOM', 'SLEEP']}
              >
                <ManagedPatientsFrequencies />
              </FeatureFlagManagerWithDefaultPage>
            }
          />

          <Route
            path={therapistRouteNames.groupDrive}
            element={
              <MultiSelectionProvider>
                <CopyDataDriveProvider>
                  <GalleryProvider>
                    <GalleryUser />
                  </GalleryProvider>
                </CopyDataDriveProvider>
              </MultiSelectionProvider>
            }
          />
          <Route
            path={therapistRouteNames.groupDriveFolder}
            element={
              <MultiSelectionProvider>
                <CopyDataDriveProvider>
                  <GalleryProvider>
                    <GalleryUser />
                  </GalleryProvider>
                </CopyDataDriveProvider>
              </MultiSelectionProvider>
            }
          />
          <Route
            path={therapistRouteNames.groupDriveItem}
            element={
              <MultiSelectionProvider>
                <CopyDataDriveProvider>
                  <GalleryProvider>
                    <GalleryUser />
                  </GalleryProvider>
                </CopyDataDriveProvider>
              </MultiSelectionProvider>
            }
          />
        </Route>
      </Route>
    </>
  )
}
